import { useFlyOutProductsBasedOnSelectedCloud } from '../../common/utils/hooks/useFlyOutProductsBasedOnSelectedCloud'
import React, { useEffect, useMemo } from 'react'
import { DateTime } from 'luxon'
import { isEmpty } from 'lodash'
import { useParams } from 'react-router-dom'
import { LoadingLogo } from '@labrav/react-components'
import {
  isLoading,
  LoadingTypes,
  useAccountId,
  useChatContext,
  useCurrentCloudProvider,
  useValidateCloudRoute,
} from '@labrav/common'

import { RevenueInsightsFilters } from './RevenueInsightsFilters/RevenueInsightsFilters'
import { useStyles } from './RevenueInsightsWrapperV2.styles'
import { RevenueInsightsTableWithFilters } from './RevenueInsightsTableWithFilters/RevenueInsightsTableWithFilters'
import { useDispatch, useSelector } from '../../store/hooks'
import {
  clearRevenueFilters,
  clearRevenueReports,
  setDateRange,
  setDateRangeType,
} from '../../modules/revenueInsightsV2/action'
import { revenueDateTypeOptionsMap } from '../../modules/revenueInsightsV2/types'
import { ListingCreationPage } from '../../common/components/ListingCreationPage/ListingCreationPage'
import { getProducts } from '../../modules/productsListing/action'
import { useSyncFlyoutRouteParams } from '../../common/utils/hooks/useSyncFlyoutRouteParams'
import type { FlyOutListingData } from '../../types/flyout'
import { CloudMarketplace } from '../../modules/productsListing/types'

export const RevenueInsightsWrapperV2: React.FC = () => {
  const { classes } = useStyles()
  const dispatch = useDispatch()
  const cloudMarketplace = useCurrentCloudProvider()
  const flyoutSellerInfo = useSelector<FlyOutListingData>(
    state => state.flyoutSellerInfo
  )
  const productsLoading = useSelector<boolean>(state =>
    isLoading(state.loading)(LoadingTypes.FETCH_PRODUCTS)
  )
  const { awsCasS3BucketName, awsKmsKeyArn, awsCasSnsTopic } =
    flyoutSellerInfo?.sellerMetadata || {}
  const { accountId: routeAccountId } = useParams<{
    cloud: string
    accountId: string
  }>()
  const fallbackAccountId = useAccountId({ product: 'flyout' }) || ''
  const accountId = routeAccountId || fallbackAccountId

  useSyncFlyoutRouteParams()
  useValidateCloudRoute('flyout', '/flyout', false)

  const isDataFeedsConnected = useMemo(() => {
    if (cloudMarketplace === 'AWS') {
      return Boolean(awsCasS3BucketName && awsKmsKeyArn && awsCasSnsTopic)
    }
    if (cloudMarketplace === 'AZURE' || cloudMarketplace === 'GCP') {
      return true
    }
    return false
  }, [cloudMarketplace, awsCasS3BucketName, awsKmsKeyArn, awsCasSnsTopic])

  const startDateDefault = useMemo(
    () => DateTime.now().minus({ months: 6 }).toISODate() as string,
    []
  )
  const loading = useSelector<boolean>(state =>
    isLoading(state.loading)(LoadingTypes.GENERAL)
  )
  const { products: productRows } = useFlyOutProductsBasedOnSelectedCloud()
  const endDateDefault = useMemo(() => DateTime.now().toISODate() as string, [])
  const {
    dateRange,
    dateRangeType,
    filters: revenueFilters,
    pageNumber: revenuePage,
    count: revenueCount,
  } = useSelector(state => state.revenueInsightsV2)
  useChatContext('revenue', () => ({
    cloud: cloudMarketplace,
    dateRange,
    dateRangeType,
    filters: revenueFilters,
    visible: { count: revenueCount ?? 0, page: revenuePage },
  }))

  const lastFetchedRef = React.useRef<{
    accountId: string
    cloudMarketplace: CloudMarketplace
  } | null>(null)

  useEffect(() => {
    if (accountId !== null) {
      const hasAccountChanged =
        lastFetchedRef.current?.accountId !== accountId ||
        lastFetchedRef.current?.cloudMarketplace !== cloudMarketplace

      if (!isEmpty(accountId) && hasAccountChanged) {
        lastFetchedRef.current = {
          accountId,
          cloudMarketplace,
        }
        dispatch(getProducts(accountId, cloudMarketplace))
      }
    }
  }, [accountId, cloudMarketplace, dispatch])

  useEffect(() => {
    return () => {
      dispatch(clearRevenueReports())
      dispatch(
        setDateRange({
          startDate: startDateDefault,
          endDate: endDateDefault,
        })
      )
      dispatch(setDateRangeType(revenueDateTypeOptionsMap.INVOICE))
      dispatch(clearRevenueFilters())
    }
  }, [dispatch, endDateDefault, startDateDefault])

  if (productsLoading) {
    return <LoadingLogo />
  }

  return (
    <div className={classes.root}>
      {productRows && !productRows?.length && !loading ? (
        <ListingCreationPage />
      ) : (
        <>
          <RevenueInsightsFilters />
          <RevenueInsightsTableWithFilters
            isDataFeedsConnected={isDataFeedsConnected}
          />
        </>
      )}
    </div>
  )
}
